import { Component } from 'react';
import styles from './convertions-widget.component.module.scss'
import { NumberInputComponent } from '../number-input/number-input.component';
import { Measure, UnitConversion, UnitInfo } from '../../types/types';
import { groupBy, has, uniqBy } from 'lodash-es';
import convert, { ConvertUtils } from '../../utils/convert-utils';
import { BrowserUtils } from '../../utils/browser.utils';
import { SearchDropdownProps, SearchableDropdown } from '../searchable-dropdown/searchable-dropdown.component';
import { unitFormatter } from '../../utils/unit-formatter';

export interface ConversionWidgetEvent {
    measure: string;
    fromUnit: string;
    toUnit: string;
    fromValue: number;
    changedProperties: string[];
}

interface ConversionWidgetProps {
    initialMeasure: Measure;
    measures: Measure[];
    unitConversions: UnitConversion[],
    measure?: string;
    selectedFromUnit?: string;
    selectedToUnit?: string;
    selectedFromValue?: number;
    onChange?: (event: ConversionWidgetEvent) => void
}

interface ConversionWidgetState {
    measure: string;
    fromUnit: string;
    toUnit: string;
    fromValue: number;
    toValue: number;
    measureDropdownOptions: SearchDropdownProps<Measure>;
    fromConvertDropdownOptions: SearchDropdownProps<UnitInfo>;
    toConvertDropdownOptions: SearchDropdownProps<UnitInfo>;
}


export default class ConversionWidgetComponent extends Component<ConversionWidgetProps, ConversionWidgetState> {
    status: ConversionWidgetState;

    private unitConversionsForMeasure: { [measure: string]: UnitConversion[] };

    constructor(props: ConversionWidgetProps) {
        super(props);

        this.unitConversionsForMeasure = groupBy(this.props.unitConversions, 'measure');

        this.state = this.getInitialStateForMeasure(
            this.props.measures,
            this.props.initialMeasure,
            this.props.selectedFromUnit,
            this.props.selectedToUnit,
            this.props.selectedFromValue
        );
    }

    componentDidUpdate(prevProps) {
        if (this.props.unitConversions !== prevProps.unitConversions) {
            this.unitConversionsForMeasure = groupBy(this.props.unitConversions, 'measure');
        }

        if (this.props.measures != prevProps.measures) {
            this.setState({
                measureDropdownOptions: this.createMeasuresDropdownOptopns(
                    this.props.measures,
                    this.props.initialMeasure
                )
            });
        }
        if (this.props.selectedFromUnit != prevProps.selectedFromUnit ||
            this.props.selectedToUnit != prevProps.selectedToUnit
        ) {
            const fromUnit = this.props.selectedFromUnit;
            const matchedConversion = this.props.unitConversions.find(a => a.fromUnit == fromUnit);
            if (!matchedConversion) return;
            const selectedMeasureStr = matchedConversion.measure;
            const selectedMeasure = this.props.measures.find(m => m.measure == selectedMeasureStr);
            if (!selectedMeasure) return;
            const toUnit = this.props.selectedToUnit;
            const fromValue = this.props.selectedFromValue;

            const state = this.getInitialStateForMeasure(
                this.props.measures,
                selectedMeasure,
                fromUnit,
                toUnit,
                fromValue,
            );
            this.setState({
                ...state
            });
        }
    }

    private getInitialStateForMeasure(
        measures: Measure[],
        measure: Measure,
        fromUnit?: string,
        toUnit?: string,
        selectedFromValue?: number,
    ): ConversionWidgetState {
        const unitInfo: UnitInfo[] = this.getUnitInfoForMeasure(measure.measure);
        const fromUnitInfo = unitInfo.find(a => a.unit == fromUnit) || unitInfo[0];
        const toUnitInfo = unitInfo.find(a => a.unit == toUnit) || unitInfo[1];

        // TODO handle zeros here
        const fromValue = selectedFromValue ? selectedFromValue : 1
        return {
            measure: measure.measure,
            fromUnit: fromUnitInfo.unit,
            toUnit: toUnitInfo.unit,
            fromValue: fromValue,
            toValue: ConvertUtils.convertTo(fromValue, fromUnitInfo.unit, toUnitInfo.unit),
            measureDropdownOptions: this.createMeasuresDropdownOptopns(measures, measure),
            fromConvertDropdownOptions: this.createFromConvertDropdownOptions(fromUnitInfo, unitInfo),
            toConvertDropdownOptions: this.createToConvertDropdownOptions(toUnitInfo, unitInfo),
        }
    }

    private getUnitInfoForMeasure(measure: string): UnitInfo[] {
        const unitInfo: UnitInfo[] = uniqBy(this.unitConversionsForMeasure[measure], 'fromUnit')
            .map(unitConversion => {
                return {
                    measure: unitConversion.measure,
                    measureName: unitConversion.measureName,
                    unit: unitConversion.fromUnit,
                    unitName: unitConversion.fromUnitName,
                };
            });
        return unitInfo;
    }

    private createMeasuresDropdownOptopns(measures: Measure[], initialMeasure: Measure): SearchDropdownProps<Measure> {
        return {
            style: 'light',
            selectedValue: initialMeasure,
            labelFormatter: (a) => <b>{a.measureName}</b>,
            dropdownFormatter: (a) => `${a.measureName}` as any,
            values: measures,
            filter: (measure, matcher) => !!measure?.measureName?.match(matcher),
            onSelect: (measureInfo) => {
                this.notifyChange({ measure: measureInfo.measure });
                // Parent will fetch data and router.replace to the new page
            },
            onStateChanged: (state) => {
                Object.assign(this.state.measureDropdownOptions, state);
            }
        }
    }

    createFromConvertDropdownOptions(fromUnitInfo: UnitInfo, unitInfo: UnitInfo[]): SearchDropdownProps<UnitInfo> {
        return {
            style: 'light',
            selectedValue: fromUnitInfo,
            labelFormatter: (a) => `From ${a.unitName}` as any,
            valueFormatter: (a) => <b className='text-info-emphasis'>{unitFormatter(a.unit)}</b>,
            dropdownFormatter: (a) => <div>
                <div><b>{unitFormatter(a.unit)}</b></div>
                <div className='text-body-secondary'>{a.unitName}</div>
            </div>,
            values: unitInfo,
            filter: (unitInfo, matcher) => {
                return !!(unitInfo?.unitName?.match(matcher) || unitInfo?.unit?.match(matcher))
            },
            onSelect: (fromUnitInfo) => {
                const update = {
                    fromUnit: fromUnitInfo.unit,
                    fromValue: ConvertUtils.convertTo(this.state.toValue, fromUnitInfo.unit, this.state.toUnit),
                }
                this.setState(update);
                this.notifyChange(update);
            },
            onStateChanged: (state) => {
                Object.assign(this.state.fromConvertDropdownOptions, state);
            }
        }
    }

    createToConvertDropdownOptions(toUnitInfo: UnitInfo, unitInfo: UnitInfo[]): SearchDropdownProps<UnitInfo> {
        return {
            style: 'light',
            selectedValue: toUnitInfo,
            labelFormatter: (a) => `To ${a.unitName}` as any,
            valueFormatter: (a) => <b className='text-info-emphasis'>{unitFormatter(a.unit)}</b>,
            dropdownFormatter: (a) => <div>
                <div><b>{unitFormatter(a.unit)}</b></div>
                <div className='text-body-secondary'>{a.unitName}</div>
            </div>,
            values: unitInfo,
            filter: (unitInfo, matcher) => {
                return !!(unitInfo?.unitName?.match(matcher) || unitInfo?.unit?.match(matcher))
            },
            onSelect: (toUnitInfo) => {
                this.setState({
                    toUnit: toUnitInfo.unit,
                    toValue: ConvertUtils.convertTo(this.state.fromValue, this.state.fromUnit, toUnitInfo.unit),
                });
                this.notifyChange({ toUnit: toUnitInfo.unit });
            },
            onStateChanged: (state) => {
                Object.assign(this.state.toConvertDropdownOptions, state);
            }
        }
    }

    resetFromAndTwoConvertDropdownOptionsState(measure: string, unitInfo: UnitInfo[]) {

    }

    notifyChange(change: Partial<ConversionWidgetEvent>) {
        if (this.props.onChange) {
            const event: ConversionWidgetEvent = {
                measure: (has(change, 'measure') && change.measure) || this.state.measure,
                fromUnit: (has(change, 'fromUnit') && change.fromUnit) || this.state.fromUnit,
                toUnit: (has(change, 'toUnit') && change.toUnit) || this.state.toUnit,
                fromValue: (has(change, 'fromValue') && change.fromValue) || this.state.fromValue,
                changedProperties: Object.keys(change),
            }
            this.props.onChange(event);
        }
    }

    fromValueChanged(newValue: number) {
        this.setState({
            fromValue: newValue,
            toValue: ConvertUtils.convertTo(newValue, this.state.fromUnit, this.state.toUnit),
        });
        this.notifyChange({ fromValue: newValue });
    }

    toValueChanged(newValue: number) {
        this.setState({
            fromValue: ConvertUtils.convertTo(newValue, this.state.toUnit, this.state.fromUnit),
            toValue: newValue,
        });
    }

    private swapUnits() {
        this.setState({
            fromUnit: this.state.toUnit,
            fromValue: this.state.toValue,
            fromConvertDropdownOptions: this.state.toConvertDropdownOptions,
            toUnit: this.state.fromUnit,
            toValue: this.state.fromValue,
            toConvertDropdownOptions: this.state.fromConvertDropdownOptions,
        });

        this.notifyChange({
            fromUnit: this.state.toUnit,
            toUnit: this.state.fromUnit,
        })
    }

    private copyTextToClipboard() {
        const fromName = convert().describe(this.state.fromUnit).plural;
        const toName = convert().describe(this.state.toUnit).plural;
        const text = `${this.state.fromValue} ${fromName} = ${this.state.toValue} ${toName}`;
        BrowserUtils.copyTextToClipboard(text);
    }

    render() {
        return <div className={styles['conversion-widget']}>
            <div>
                <SearchableDropdown {...this.state.measureDropdownOptions}></SearchableDropdown>
            </div>
            <div className={styles['unit-selector']}>
                <div className={styles['unit']}>
                    <div>
                        <SearchableDropdown {...this.state.fromConvertDropdownOptions}></SearchableDropdown>
                    </div>
                    <div>
                        <NumberInputComponent
                            value={this.state.fromValue}
                            onChange={(e) => this.fromValueChanged(e)}
                        ></NumberInputComponent>
                    </div>
                </div>
                <div className={styles['switch-units']}>
                    <svg xmlns="http://www.w3.org/2000/svg" onClick={() => this.swapUnits()}
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M3 8l4 -4l4 4"></path>
                        <path d="M7 4l0 9"></path>
                        <path d="M13 16l4 4l4 -4"></path>
                        <path d="M17 10l0 10"></path>
                    </svg>
                </div>
                <div className={styles['unit']}>
                    <div>
                        <SearchableDropdown {...this.state.toConvertDropdownOptions}></SearchableDropdown>
                    </div>
                    <div>
                        <NumberInputComponent
                            value={this.state.toValue}
                            onChange={(e) => this.toValueChanged(e)}></NumberInputComponent>
                    </div>
                </div>
            </div>
            <div className={styles['actions-container']}>
                <button type="button" className={styles['action-button']} onClick={() => this.swapUnits()} aria-label="Swap units">
                    <svg xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M3 8l4 -4l4 4"></path>
                        <path d="M7 4l0 9"></path>
                        <path d="M13 16l4 4l4 -4"></path>
                        <path d="M17 10l0 10"></path>
                    </svg>
                    <span>Swap units</span>
                </button>
                <button type="button" className={styles['action-button-primary']} onClick={() => this.copyTextToClipboard()} aria-label="Copy result">
                    <svg xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z"></path>
                        <path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1"></path>
                    </svg>
                    <span>Copy result</span>
                </button>
            </div>
        </div>
    }
}
